import { useEffect, useId, useState } from 'react'
import { z } from 'zod'
import { isInternalEmail, normalizeEmail } from '@ai-meeting/shared'
import { ExclamationTriangleIcon } from '@/components/Icons'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'

export type ShareMinutesEmailLocale = 'vi' | 'ja'

export interface ShareMinutesEmailPayload {
  locale: ShareMinutesEmailLocale
  emails: string[]
}

interface ShareMinutesEmailModalProps {
  meetingTitle: string
  defaultLocale: ShareMinutesEmailLocale
  onClose: () => void
  /** Called after client-side validation succeeds. May return a Promise; reject to keep modal open. */
  onSend: (payload: ShareMinutesEmailPayload) => void | Promise<void>
}

function isValidEmail(value: string): boolean {
  return z.string().email().safeParse(value.trim()).success
}

/**
 * Share meeting minutes via email — language + multi-recipient chip form.
 * Validates input then calls `onSend` (parent posts to share-minutes-email API).
 */
export default function ShareMinutesEmailModal({
  meetingTitle,
  defaultLocale,
  onClose,
  onSend,
}: ShareMinutesEmailModalProps) {
  const { t } = useUiStrings()
  const titleId = useId()
  const emailId = useId()
  const languageLegendId = useId()
  const warningId = useId()
  const [locale, setLocale] = useState<ShareMinutesEmailLocale>(defaultLocale)
  const [emails, setEmails] = useState<string[]>([])
  const [draft, setDraft] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)

  const trimmedDraft = draft.trim()
  const draftIsValid = trimmedDraft.length > 0 && isValidEmail(trimmedDraft)
  const canSend = emails.length > 0 || draftIsValid

  const emailsForDomainCheck =
    draftIsValid && !emails.some((e) => normalizeEmail(e) === normalizeEmail(trimmedDraft))
      ? [...emails, trimmedDraft]
      : emails
  const externalCount = emailsForDomainCheck.filter((email) => !isInternalEmail(email)).length
  const hasExternal = externalCount > 0

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !sending) onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose, sending])

  function updateEmails(next: string[]) {
    setEmails(next)
  }

  function tryCommitToken(raw: string): boolean {
    const value = raw.trim()
    if (!value) return true
    if (!isValidEmail(value)) {
      setEmailError(t.emailInvalid)
      return false
    }
    const normalized = normalizeEmail(value)
    if (emails.some((e) => normalizeEmail(e) === normalized)) {
      setDraft('')
      setEmailError(null)
      return true
    }
    updateEmails([...emails, value.trim()])
    setDraft('')
    setEmailError(null)
    return true
  }

  /** Commit complete tokens split by comma or whitespace; keep the trailing fragment as draft. */
  function handleDraftChange(value: string) {
    if (!/[,\s]/.test(value)) {
      setDraft(value)
      if (emailError) setEmailError(null)
      return
    }

    const endsWithDelimiter = /[,\s]$/.test(value)
    const parts = value.split(/[,\s]+/).filter(Boolean)
    const remainder = endsWithDelimiter ? '' : (parts.pop() ?? '')
    let nextEmails = [...emails]
    let failed: string | null = null

    for (const part of parts) {
      const token = part.trim()
      if (!token) continue
      if (!isValidEmail(token)) {
        failed = token
        break
      }
      const normalized = normalizeEmail(token)
      if (!nextEmails.some((e) => normalizeEmail(e) === normalized)) {
        nextEmails = [...nextEmails, token]
      }
    }

    if (failed) {
      setDraft(value)
      setEmailError(t.emailInvalid)
      return
    }

    if (nextEmails.length !== emails.length) {
      updateEmails(nextEmails)
    }
    setDraft(remainder)
    setEmailError(null)
  }

  function handleDraftKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault()
      if (!trimmedDraft) return
      tryCommitToken(trimmedDraft)
      return
    }
    // Space commits a complete valid draft (same as typing a trailing space in onChange).
    if (event.key === ' ' && draftIsValid) {
      event.preventDefault()
      tryCommitToken(trimmedDraft)
      return
    }
    if (event.key === 'Backspace' && draft.length === 0 && emails.length > 0) {
      event.preventDefault()
      updateEmails(emails.slice(0, -1))
    }
  }

  function removeEmail(index: number) {
    updateEmails(emails.filter((_, i) => i !== index))
  }

  function buildResolvedEmails(): string[] | null {
    if (!trimmedDraft) {
      if (emails.length === 0) {
        setEmailError(t.emailRequired)
        return null
      }
      return emails
    }
    if (!isValidEmail(trimmedDraft)) {
      setEmailError(t.emailInvalid)
      return null
    }
    const normalized = normalizeEmail(trimmedDraft)
    if (emails.some((e) => normalizeEmail(e) === normalized)) {
      return emails
    }
    return [...emails, trimmedDraft]
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (sending) return
    const resolved = buildResolvedEmails()
    if (!resolved || resolved.length === 0) return

    setEmails(resolved)
    setDraft('')
    setEmailError(null)
    setSendError(null)

    void (async () => {
      setSending(true)
      try {
        await onSend({ locale, emails: resolved })
      } catch (err) {
        setSendError(
          err instanceof Error ? err.message : t.failedShareMinutesEmail
        )
      } finally {
        setSending(false)
      }
    })()
  }

  const warningText =
    externalCount === 1
      ? t.shareMinutesExternalDomainWarningOne
      : fmt(t.shareMinutesExternalDomainWarningMany, { count: String(externalCount) })

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/30 px-3 py-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <button
        type="button"
        aria-label={t.close}
        className="fixed inset-0 cursor-default"
        onClick={onClose}
      />

      <div className="animate-scale-in relative w-full max-w-md rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5 shadow-2xl sm:p-6">
        <h2 id={titleId} className="text-lg font-bold text-[var(--text-primary)]">
          {t.sendEmail}
        </h2>
        <p className="mt-2 text-sm text-[var(--text-secondary)]">
          {fmt(t.shareMinutesEmailDescription, { meetingName: meetingTitle })}
        </p>

        <form onSubmit={handleSubmit} className="mt-5 space-y-4" noValidate>
          <fieldset className="space-y-2" disabled={sending}>
            <legend
              id={languageLegendId}
              className="text-sm font-semibold text-[var(--text-primary)]"
            >
              {t.language}
            </legend>
            <div role="radiogroup" aria-labelledby={languageLegendId} className="space-y-2">
              <LanguageOption
                label={t.languageVietnamese}
                selected={locale === 'vi'}
                onSelect={() => setLocale('vi')}
              />
              <LanguageOption
                label={t.languageJapanese}
                selected={locale === 'ja'}
                onSelect={() => setLocale('ja')}
              />
            </div>
          </fieldset>

          <div>
            <label
              htmlFor={emailId}
              className="mb-1.5 block text-sm font-semibold text-[var(--text-primary)]"
            >
              {t.recipientEmails}
            </label>
            <div
              className={[
                'input-field flex min-h-[2.75rem] flex-wrap items-center gap-1.5 py-1.5',
                emailError ? 'border-[var(--error)]' : '',
              ].join(' ')}
              onClick={() => document.getElementById(emailId)?.focus()}
            >
              {emails.map((email, index) => {
                const external = !isInternalEmail(email)
                return (
                  <span
                    key={`${normalizeEmail(email)}-${index}`}
                    className={[
                      'inline-flex max-w-full items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
                      external
                        ? 'bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-100'
                        : 'bg-[var(--accent-light)] text-[var(--accent)]',
                    ].join(' ')}
                  >
                    <span className="truncate">{email}</span>
                    <button
                      type="button"
                      aria-label={`${t.delete} ${email}`}
                      onClick={(event) => {
                        event.stopPropagation()
                        removeEmail(index)
                      }}
                      className={[
                        'shrink-0 rounded-full p-0.5 leading-none',
                        external
                          ? 'text-amber-800 hover:bg-amber-200/80 dark:text-amber-200 dark:hover:bg-amber-900/60'
                          : 'text-[var(--accent)] hover:bg-[var(--accent)]/15',
                      ].join(' ')}
                    >
                      ×
                    </button>
                  </span>
                )
              })}
              <input
                id={emailId}
                type="text"
                autoComplete="email"
                inputMode="email"
                value={draft}
                disabled={sending}
                onChange={(event) => handleDraftChange(event.target.value)}
                onKeyDown={handleDraftKeyDown}
                placeholder={emails.length === 0 ? t.recipientEmailPlaceholder : undefined}
                className="min-w-[8rem] flex-1 border-0 bg-transparent p-0 text-sm text-[var(--text-primary)] outline-none placeholder:text-[var(--text-secondary)]"
                aria-invalid={Boolean(emailError)}
                aria-describedby={
                  [emailError ? `${emailId}-error` : null, hasExternal ? warningId : null]
                    .filter(Boolean)
                    .join(' ') || undefined
                }
              />
            </div>
            {emailError && (
              <p id={`${emailId}-error`} className="mt-1.5 text-xs text-[var(--error)]" role="alert">
                {emailError}
              </p>
            )}
            {hasExternal && (
              <div
                id={warningId}
                role="status"
                className="mt-2 flex gap-2.5 rounded-lg bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
              >
                <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300" />
                <p>{warningText}</p>
              </div>
            )}
            {sendError && (
              <p className="mt-2 text-xs text-[var(--error)]" role="alert">
                {sendError}
              </p>
            )}
          </div>

          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="rounded-lg border border-[var(--border-color)] px-4 py-2 text-sm font-semibold text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:pointer-events-none disabled:opacity-50"
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              disabled={!canSend || sending}
              className="btn-primary px-4 py-2 disabled:pointer-events-none disabled:opacity-50"
            >
              {sending ? t.sendingEmail : hasExternal ? t.sendAnyway : t.send}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function LanguageOption({
  label,
  selected,
  onSelect,
}: {
  label: string
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={[
        'flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm font-medium transition-colors',
        selected
          ? 'border-[var(--accent)] bg-[var(--accent-light)] text-[var(--text-primary)]'
          : 'border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]',
      ].join(' ')}
    >
      <span
        className={[
          'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
          selected ? 'border-[var(--accent)]' : 'border-[var(--border-color)]',
        ].join(' ')}
        aria-hidden
      >
        {selected && <span className="h-2 w-2 rounded-full bg-[var(--accent)]" />}
      </span>
      {label}
    </button>
  )
}
